import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isApiError } from '@/lib/api-client';

/** Translates a validation message: i18n keys (`validation.xxx`) or ready-made text. */
export function useValidationMessage() {
  const { t } = useTranslation('validation');
  return useCallback(
    (message: string | undefined): string | undefined => {
      if (!message) return undefined;
      return message.startsWith('validation.') ? t(message.slice('validation.'.length)) : message;
    },
    [t],
  );
}

/** A user-facing sentence for any error, from its API error code. */
export function useErrorText() {
  const { t } = useTranslation('errors');
  return useCallback(
    (error: unknown): string => {
      if (!isApiError(error)) return t('INTERNAL_ERROR');
      const details = (error.details ?? {}) as Record<string, unknown>;
      const retryAfter = Number(details.retryAfterSeconds ?? 0);
      return t(error.code, {
        defaultValue: error.message,
        minutes: Math.max(1, Math.ceil(retryAfter / 60)),
        count: Number(details.usageCount ?? details.left ?? 0),
        number: (details.count as { number?: string } | undefined)?.number ?? '',
      });
    },
    [t],
  );
}
